import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square, CheckCircle2, Clock3 } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  { key: "todo",        label: "To do",       tint: "bg-neutral-50",   stripe: "bg-neutral-300" },
  { key: "in_progress", label: "In progress", tint: "bg-sky-50",       stripe: "bg-sky-500" },
  { key: "review",      label: "Review",      tint: "bg-violet-50",    stripe: "bg-violet-500" },
  { key: "done",        label: "Done",        tint: "bg-emerald-50",   stripe: "bg-emerald-500" },
];

const PRIORITY_DOT = {
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-sky-500",
};

export default function Tasks() {
  const { user } = useAuth();
  const { timer, start: startTimer, pause, resume, stop } = useTimer();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientFilter, setClientFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState(isAdmin ? "all" : "me");

  const load = async () => {
    const [t, p, c, u] = await Promise.all([
      api.get("/tasks"),
      api.get("/projects?include_hidden=true"),
      api.get("/clients"),
      api.get("/users"),
    ]);
    setTasks(t.data || []);
    setProjects(p.data || []);
    setClients(c.data || []);
    setUsers(u.data || []);
  };

  useEffect(() => { load(); }, []);

  const projectById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const clientById = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const clientOf = (t) => {
    const p = projectById[t.project_id];
    return p ? clientById[p.client_id] : null;
  };

  const filtered = useMemo(() => tasks.filter(t => {
    if (clientFilter !== "all") {
      const c = clientOf(t);
      if (!c || c.id !== clientFilter) return false;
    }
    if (assigneeFilter === "me") {
      if (t.assignee_id !== user?.id) return false;
    } else if (assigneeFilter !== "all") {
      if (t.assignee_id !== assigneeFilter) return false;
    }
    return true;
  }), [tasks, clientFilter, assigneeFilter, projectById, clientById, user?.id]);

  const byCol = useMemo(() => {
    const g = { todo: [], in_progress: [], review: [], done: [] };
    for (const t of filtered) (g[t.status] || g.todo).push(t);
    // sort: priority high→low, then due date, then title
    const prio = { high: 0, medium: 1, low: 2 };
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (prio[a.priority] ?? 3) - (prio[b.priority] ?? 3)
        || (a.due_date || "9999").localeCompare(b.due_date || "9999")
        || (a.title || "").localeCompare(b.title || ""));
    }
    return g;
  }, [filtered]);

  const projectIsActive = (t) => {
    const p = projectById[t.project_id];
    return p?.status === "active" && !p?.archived;
  };

  const canControl = (t) => isAdmin || t.assignee_id === user?.id;

  const doStart = async (t) => {
    if (!projectIsActive(t)) { toast.error("Parent task isn't Active"); return; }
    try {
      await startTimer({ project_id: t.project_id, task_id: t.id, description: t.title });
      load(); // timer start flips todo → in_progress server-side
    } catch (e) { console.warn("[Activities] start failed", e); }
  };

  const markStatus = async (t, status) => {
    try {
      if (timer && timer.task_id === t.id) await stop();
      await api.put(`/tasks/${t.id}`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const clientLabel = (c) => c?.company || c?.name || "—";

  return (
    <div data-testid="tasks-page">
      <PageHeader
        eyebrow="Workload"
        title="Activities"
        description="Every piece of work in flight, grouped by status."
        testid="tasks-header"
      />

      <Section>
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="activities-filter-bar">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Client</span>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-56 rounded-sm h-9" data-testid="filter-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{clientLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Assignee</span>
            <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
              <SelectTrigger className="w-48 rounded-sm h-9" data-testid="filter-assignee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="me">Me only</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-xs text-neutral-500">{filtered.length} activit{filtered.length === 1 ? "y" : "ies"}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="activities-kanban">
          {COLS.map(col => (
            <div key={col.key} className={`border border-neutral-200 ${col.tint}`} data-testid={`kanban-col-${col.key}`}>
              <div className={`h-1 ${col.stripe}`} />
              <div className="px-3 py-2 border-b border-neutral-200 flex items-center justify-between bg-white/70">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">{col.label}</span>
                <span className="text-[10px] font-mono text-neutral-500">{byCol[col.key].length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[100px]">
                {byCol[col.key].length === 0 && (
                  <div className="text-[11px] text-neutral-400 text-center py-6">Nothing here.</div>
                )}
                {byCol[col.key].map(t => {
                  const c = clientOf(t);
                  const active = timer && timer.task_id === t.id;
                  const paused = active && timer.is_paused;
                  const assignee = userById[t.assignee_id];
                  return (
                    <div key={t.id} className="border border-neutral-200 bg-white p-3 hover:shadow-sm transition-shadow" data-testid={`activity-card-${t.id}`}>
                      <div className="flex items-start gap-2 mb-2">
                        <span className={`w-1.5 h-6 rounded-sm shrink-0 mt-0.5 ${PRIORITY_DOT[t.priority] || "bg-neutral-300"}`} />
                        <div className="min-w-0 flex-1">
                          <Link to={`/projects/${t.project_id}`} className="block text-sm font-semibold hover:underline truncate">{t.title}</Link>
                          <div className="text-[10px] text-neutral-500 truncate">
                            {c ? clientLabel(c) : "—"}
                            {assignee && <span> · {assignee.name || assignee.email}</span>}
                            {t.due_date && <span className="font-mono"> · Due {t.due_date}</span>}
                          </div>
                        </div>
                      </div>
                      {canControl(t) && (
                        <div className="flex items-center gap-1 mt-2">
                          {active ? (
                            <>
                              {paused ? (
                                <Button size="sm" onClick={resume} className="h-7 rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[11px]" data-testid={`activity-resume-${t.id}`}>
                                  <Play size={11} className="mr-1" /> Resume
                                </Button>
                              ) : (
                                <Button size="sm" onClick={pause} className="h-7 rounded-sm bg-neutral-700 hover:bg-neutral-600 text-white text-[11px]" data-testid={`activity-pause-${t.id}`}>
                                  <Pause size={11} className="mr-1" /> Pause
                                </Button>
                              )}
                              <Button size="sm" onClick={async () => { await stop(); load(); }} className="h-7 w-7 p-0 rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid={`activity-stop-${t.id}`}>
                                <Square size={11} />
                              </Button>
                            </>
                          ) : (
                            t.status !== "done" && (
                              <Button size="sm" onClick={() => doStart(t)} disabled={!projectIsActive(t)}
                                      className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] disabled:opacity-40"
                                      data-testid={`activity-start-${t.id}`}>
                                <Play size={11} className="mr-1" /> Start
                              </Button>
                            )
                          )}
                          {isAdmin && !active && t.status !== "done" && (
                            <Button size="sm" variant="outline" onClick={() => markStatus(t, "done")}
                                    className="h-7 w-7 p-0 rounded-sm text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    data-testid={`activity-done-${t.id}`} title="Mark done">
                              <CheckCircle2 size={12} />
                            </Button>
                          )}
                          {isAdmin && t.status === "in_progress" && !active && (
                            <Button size="sm" variant="outline" onClick={() => markStatus(t, "review")}
                                    className="h-7 rounded-sm text-violet-700 border-violet-200 hover:bg-violet-50 text-[11px]"
                                    data-testid={`activity-review-${t.id}`}>
                              Review
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
